import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft } from "lucide-react";

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

export default function ApplyPage() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    license_state: "",
    years_experience: 0,
    current_brokerage: "",
    annual_volume: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => ({ ...f, [k]: k === "years_experience" ? Number(v || 0) : v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      toast.success("Application received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="apply-page" className="min-h-screen bg-[#ffffff] text-[#0a0a0a]">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 numeric text-xs uppercase tracking-widest text-black/50 hover:text-[#0a0a0a]"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="overline mt-8 mb-4">Apply to 1st Choice</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#0a0a0a]">
            Tell us about <em className="not-italic text-[#121275]">your business</em>.
          </h1>
          <p className="mt-6 text-black/60 max-w-xl">
            We review every application personally. A senior recruiter will reach out within
            one business day.
          </p>

          {done ? (
            <div
              data-testid="apply-success"
              className="mt-16 bg-[#f7f7f7] border border-[#121275]/30 p-12 text-center tracing-border"
            >
              <CheckCircle2 className="text-[#121275] mx-auto" size={56} strokeWidth={1.25} />
              <div className="overline mt-6">Application received</div>
              <h2 className="font-display text-3xl mt-3 font-light text-[#0a0a0a]">
                Welcome to the next step.
              </h2>
              <p className="mt-4 text-black/60 max-w-md mx-auto">
                Your application is in front of our recruiting team. Expect a personal call or
                email within one business day.
              </p>
              <Link to="/" className="btn-secondary mt-10 inline-flex">
                Back to home
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="apply-form"
              className="mt-16 bg-[#f7f7f7] border border-black/10 p-8 sm:p-12 space-y-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div>
                  <label className="field-label">Full name</label>
                  <input required className="input-line" data-testid="apply-fullname" value={form.full_name} onChange={update("full_name")} />
                </div>
                <div>
                  <label className="field-label">Email</label>
                  <input required type="email" className="input-line" data-testid="apply-email" value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label className="field-label">Phone</label>
                  <input required className="input-line" data-testid="apply-phone" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label className="field-label">License state</label>
                  <select required className="input-line" data-testid="apply-state" value={form.license_state} onChange={update("license_state")}>
                    <option value="" className="bg-white text-black">Select state…</option>
                    {STATES.map((s) => (
                      <option key={s} value={s} className="bg-white text-black">{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Years licensed</label>
                  <input
                    required
                    type="number"
                    min={0}
                    max={80}
                    className="input-line"
                    data-testid="apply-years"
                    value={form.years_experience}
                    onChange={update("years_experience")}
                  />
                </div>
                <div>
                  <label className="field-label">Current brokerage</label>
                  <input className="input-line" data-testid="apply-brokerage" value={form.current_brokerage} onChange={update("current_brokerage")} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">Last 12 months volume</label>
                  <input
                    placeholder="e.g. $24M / 32 transactions"
                    className="input-line"
                    data-testid="apply-volume"
                    value={form.annual_volume}
                    onChange={update("annual_volume")}
                  />
                </div>
              </div>

              <div>
                <label className="field-label">Anything we should know?</label>
                <textarea
                  rows={4}
                  className="input-line resize-none"
                  data-testid="apply-message"
                  value={form.message}
                  onChange={update("message")}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="apply-submit-button"
                className="btn-primary w-full sm:w-auto disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit application"}
              </button>
              <p className="numeric text-[10px] text-black/30 uppercase tracking-widest">
                By submitting, you agree to be contacted by a member of our recruiting team.
              </p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
